import {
  ArrowDownward,
  ArrowUpward,
  Description as DocumentIcon,
  GetApp as DownloadIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  PlayArrow as PlayIcon,
  VideoFile as VideoIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import { FileItem } from '../Api';

export type SortField = 'name' | 'date' | 'size' | 'upload' | 'type';
export type SortDirection = 'asc' | 'desc';

interface FileListProps {
  files: FileItem[];
  onTranscode: (fileItem: FileItem) => void;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

const FileList: React.FC<FileListProps> = ({
  files,
  onTranscode,
  sortField,
  sortDirection,
  onSort,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getFileIcon = (filename: string) => {
    const extension = filename.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv':
        return <VideoIcon />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return <ImageIcon />;
      case 'pdf':
      case 'doc':
      case 'docx':
      case 'txt':
        return <DocumentIcon />;
      default:
        return <FolderIcon />;
    }
  };

  const getFileTypeColor = (filename: string) => {
    const extension = filename.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv':
        return 'error';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return 'success';
      case 'pdf':
      case 'doc':
      case 'docx':
      case 'txt':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatUploadDate = (uploadTimestamp: string) => {
    try {
      const date = new Date(+uploadTimestamp);
      return date.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const formatFileSize = (sizeString: string) => {
    const size = parseInt(sizeString);
    if (isNaN(size)) return sizeString;
    
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode }> = ({ field, children }) => (
    <TableCell
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        fontWeight: 600,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
      onClick={() => onSort(field)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {children}
        {sortField === field && (
          sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
        )}
      </Box>
    </TableCell>
  );

  if (isMobile) {
    // Compact mobile view
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableHeader field="name">File</SortableHeader>
              <SortableHeader field="date">Date</SortableHeader>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.key} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: `${getFileTypeColor(file.filename)}.main`,
                        width: 32,
                        height: 32,
                      }}
                    >
                      {getFileIcon(file.filename)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {file.title || file.filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.size)}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(file.date)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {file.signedUrl ? (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.location.href = `video/${file.key}`}
                    >
                      <PlayIcon />
                    </IconButton>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onTranscode(file)}
                    >
                      Transcode
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Desktop view
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="40px"></TableCell>
            <SortableHeader field="name">Name</SortableHeader>
            <SortableHeader field="type">Type</SortableHeader>
            <SortableHeader field="date">Date</SortableHeader>
            <SortableHeader field="size">Size</SortableHeader>
            <SortableHeader field="upload">Uploaded</SortableHeader>
            <TableCell width="200px">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => {
            const fileExtension = file.filename.toLowerCase().split('.').pop();
            return (
              <TableRow key={file.key} hover>
                <TableCell>
                  <Avatar
                    sx={{
                      bgcolor: `${getFileTypeColor(file.filename)}.main`,
                      width: 32,
                      height: 32,
                    }}
                  >
                    {getFileIcon(file.filename)}
                  </Avatar>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {file.title || file.filename}
                    </Typography>
                    {file.title && (
                      <Typography variant="caption" color="text.secondary">
                        {file.filename}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={fileExtension?.toUpperCase() || 'FILE'}
                    size="small"
                    color={getFileTypeColor(file.filename) as any}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(file.date)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatFileSize(file.size)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatUploadDate(file.upload)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {file.signedUrl ? (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<PlayIcon />}
                        onClick={() => window.location.href = `video/${file.key}`}
                      >
                        Play
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onTranscode(file)}
                      >
                        Transcode
                      </Button>
                    )}
                    <IconButton size="small" color="primary" title="Download">
                      <DownloadIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FileList;
